import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { useNavigate } from 'react-router-dom'

const COLORS = {
  primary: '#2563EB',
  primaryDark: '#1D4ED8',
  primaryLight: '#EFF6FF',
  success: '#15803D',
  successLight: '#F0FDF4',
  warning: '#D97706',
  warningLight: '#FFFBEB',
  danger: '#BE123C',
  dangerLight: '#FFF1F2',
  text: '#0F172A',
  textMid: '#374151',
  textMuted: '#64748B',
  textFaint: '#94A3B8',
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  bg: '#F7F8FC',
  bgCard: '#FFFFFF',
  bgHover: '#FAFBFC',
  sidebar: '#1E293B',
  sidebarActive: '#2D3F55',
}

const notifTypeConfig = {
  info:     { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE', label: 'Info' },
  warning:  { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A', label: 'Warning' },
  critical: { bg: '#FFF1F2', color: '#BE123C', border: '#FECDD3', label: 'Critical' },
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: COLORS.bgCard, borderRadius: '14px', border: `1px solid ${COLORS.border}`, padding: '24px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <p style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.7px', margin: 0 }}>{label}</p>
      <p style={{ fontSize: '38px', fontWeight: '800', color: COLORS.text, margin: 0, letterSpacing: '-1.5px', lineHeight: 1 }}>{value}</p>
      <div style={{ height: '3px', borderRadius: '999px', background: COLORS.borderLight }}>
        <div style={{ height: '100%', borderRadius: '999px', background: color, width: '60%' }} />
      </div>
    </div>
  )
}

function ActionsDropdown({ company, onView, onDelete, onNotify, onImpersonate, onSuspend, onReactivate, onEmail }) {
  const [open, setOpen] = useState(false)
  const ref = useRef()
  const isSuspended = company.status === 'suspended'

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const actions = [
    { label: 'View Details', onClick: onView },
    { label: 'Send Email', onClick: onEmail },
    { label: 'Send Notification', onClick: onNotify },
    { label: 'Login as Contractor', onClick: onImpersonate, note: 'coming soon' },
    { divider: true },
    isSuspended
      ? { label: 'Reactivate Account', onClick: onReactivate, success: true }
      : { label: 'Suspend Account', onClick: onSuspend, warn: true },
    { divider: true },
    { label: 'Delete Company', onClick: onDelete, danger: true },
  ]

  return (
    <div ref={ref} style={{ position: 'static' }}>
      <button onClick={() => setOpen(!open)} style={{ padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.border}`, background: 'white', color: COLORS.textMid, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
        Actions <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
      </button>
      {open && (
        <div style={{ position: 'fixed', background: 'white', borderRadius: '10px', border: `1px solid ${COLORS.border}`, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.14)', minWidth: '200px', overflow: 'hidden', transform: 'translateX(-100%)' }}>
          {actions.map((a, i) => a.divider
            ? <div key={i} style={{ height: '1px', background: COLORS.borderLight }} />
            : (
              <div key={i} onClick={() => { if (!a.note) { a.onClick(); setOpen(false) } }} style={{ padding: '10px 16px', fontSize: '13px', fontWeight: '500', color: a.danger ? COLORS.danger : a.warn ? COLORS.warning : a.success ? COLORS.success : a.note ? COLORS.textFaint : COLORS.textMid, cursor: a.note ? 'default' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.1s' }}
                onMouseEnter={e => { if (!a.note) e.currentTarget.style.background = a.danger ? '#FFF1F2' : a.warn ? COLORS.warningLight : a.success ? COLORS.successLight : COLORS.bgHover }}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {a.label}
                {a.note && <span style={{ fontSize: '10px', color: COLORS.textFaint, background: COLORS.borderLight, padding: '2px 6px', borderRadius: '4px' }}>{a.note}</span>}
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

function Badge({ children, color, bg, border }) {
  return (
    <span style={{ padding: '4px 12px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', background: bg, color, border: `1px solid ${border}`, display: 'inline-block', whiteSpace: 'nowrap', minWidth: '80px', textAlign: 'center' }}>{children}</span>
  )
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
      <div>
        <h1 style={{ fontSize: '22px', fontWeight: '800', color: COLORS.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background: COLORS.bgCard, borderRadius: '14px', border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', ...style }}>{children}</div>
  )
}

function PrimaryButton({ children, onClick, disabled, small }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ height: small ? '34px' : '40px', padding: `0 ${small ? 14 : 20}px`, borderRadius: '9px', background: disabled ? '#CBD5E1' : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, color: 'white', fontSize: small ? '12px' : '13px', fontWeight: '700', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>{children}</button>
  )
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{ width: '100%', height: '42px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '0 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', fontFamily: 'inherit', color: COLORS.text, ...style }} />
  )
}

// ─── OVERVIEW TAB ───────────────────────────────────────────────────────────
function OverviewTab({ stats, notifications, onTabChange }) {
  return (
    <div>
      <SectionHeader title="Overview" subtitle={`Last updated: ${new Date().toLocaleString('en-GB')}`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
        <StatCard label="Total Companies" value={stats.companies} color={COLORS.primary} />
        <StatCard label="Total Users" value={stats.users} color={COLORS.success} />
        <StatCard label="Total Quotes" value={stats.quotes} color={COLORS.warning} />
      </div>
      <Card>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Active Notifications</h2>
          <PrimaryButton small onClick={() => onTabChange('notifications')}>Manage</PrimaryButton>
        </div>
        <div style={{ padding: '16px 24px' }}>
          {notifications.filter(n => n.is_active).length === 0 ? (
            <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0, textAlign: 'center', padding: '16px 0' }}>No active notifications</p>
          ) : (
            notifications.filter(n => n.is_active).map((n, i) => {
              const cfg = notifTypeConfig[n.type] || notifTypeConfig.info
              return (
                <div key={i} style={{ padding: '12px 16px', borderRadius: '9px', background: cfg.bg, border: `1px solid ${cfg.border}`, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Badge color={cfg.color} bg={cfg.bg} border={cfg.border}>{cfg.label}</Badge>
                  <p style={{ fontSize: '13px', color: cfg.color, margin: 0, fontWeight: '500', flex: 1 }}>{n.message}</p>
                </div>
              )
            })
          )}
        </div>
      </Card>
    </div>
  )
}

// ─── COMPANIES TAB ──────────────────────────────────────────────────────────
function CompaniesTab({ companies, loading, onSelectCompany, onDeleteCompany, onNotifyCompany, onSuspendCompany, onReactivateCompany, onEmailCompany }) {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [filterStatus, setFilterStatus] = useState('all')

  const filtered = companies
    .filter(c => (filterStatus === 'all' || c.status === filterStatus || (!c.status && filterStatus === 'active')) && (c.company_name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
      if (sortBy === 'name') return a.company_name?.localeCompare(b.company_name)
      return 0
    })

  return (
    <div>
      <SectionHeader title="Companies" subtitle={`${companies.length} registered contractor${companies.length !== 1 ? 's' : ''}`} />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." style={{ flex: 1, height: '38px' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ height: '38px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '0 12px', fontSize: '13px', color: COLORS.textMid, background: 'white', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ height: '38px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '0 12px', fontSize: '13px', color: COLORS.textMid, background: 'white', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>
      <div style={{ background: COLORS.bgCard, borderRadius: '14px', border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'visible' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 120px 140px', padding: '10px 24px', background: '#F8FAFC', borderBottom: `1px solid ${COLORS.border}`, borderRadius: '14px 14px 0 0', gap: '16px' }}>
          {['Company', 'Email', 'Joined', 'Status', 'Actions'].map((h, i) => (
            <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: COLORS.textFaint }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: COLORS.textFaint }}>{search ? 'No companies match your search' : 'No companies yet'}</div>
        ) : (
          filtered.map((company, i) => {
            const suspended = company.status === 'suspended'
            return (
              <div key={company.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 120px 140px', gap: '16px', padding: '14px 24px', alignItems: 'center', borderBottom: i < filtered.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none', transition: 'background 0.1s', position: 'relative', background: suspended ? '#FFFBF5' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = suspended ? '#FFF8ED' : COLORS.bgHover}
                onMouseLeave={e => e.currentTarget.style.background = suspended ? '#FFFBF5' : 'transparent'}
              >
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: suspended ? COLORS.textMuted : COLORS.text, margin: '0 0 2px' }}>{company.company_name}</p>
                  {company.vat_number && <p style={{ fontSize: '11px', color: COLORS.textFaint, margin: 0 }}>VAT: {company.vat_number}</p>}
                  {suspended && company.suspend_reason && <p style={{ fontSize: '11px', color: COLORS.warning, margin: '2px 0 0' }}>Reason: {company.suspend_reason}</p>}
                </div>
                <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{company.email || '—'}</span>
                <span style={{ fontSize: '12px', color: COLORS.textFaint }}>{new Date(company.created_at).toLocaleDateString('en-GB')}</span>
                <Badge color={suspended ? COLORS.warning : COLORS.success} bg={suspended ? COLORS.warningLight : COLORS.successLight} border={suspended ? '#FDE68A' : '#BBF7D0'}>{suspended ? 'Suspended' : 'Active'}</Badge>
                <ActionsDropdown company={company} onView={() => onSelectCompany(company)} onDelete={() => onDeleteCompany(company)} onNotify={() => onNotifyCompany(company)} onEmail={() => onEmailCompany(company)} onImpersonate={() => alert(`Login as Contractor — coming soon.\nThis will let you view the app as ${company.company_name}.`)} onSuspend={() => onSuspendCompany(company)} onReactivate={() => onReactivateCompany(company)} />
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── COMPANY DETAIL ──────────────────────────────────────────────────────────
function CompanyDetailTab({ company, users, onBack, onNotify, onDelete }) {
  const fields = [
    { label: 'Company Name', value: company.company_name },
    { label: 'Email', value: company.email || '—' },
    { label: 'Phone', value: company.phone || '—' },
    { label: 'Address', value: company.address || '—' },
    { label: 'VAT Number', value: company.vat_number || '—' },
    { label: 'Website', value: company.website || '—' },
    { label: 'Joined', value: new Date(company.created_at).toLocaleDateString('en-GB') },
    { label: 'Next Quote #', value: `#${company.next_quote_number || 1}` },
  ]

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: COLORS.textMuted, fontWeight: '600', padding: 0, marginBottom: '20px', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>← Back to Companies</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '800', color: COLORS.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>{company.company_name}</h1>
          <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0 }}>Company ID: {company.id}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onNotify} style={{ height: '38px', padding: '0 16px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, background: 'white', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, cursor: 'pointer' }}>Send Notification</button>
          <button onClick={onDelete} style={{ height: '38px', padding: '0 16px', borderRadius: '9px', border: `1.5px solid ${COLORS.dangerLight}`, background: COLORS.dangerLight, fontSize: '13px', fontWeight: '600', color: COLORS.danger, cursor: 'pointer' }}>Delete</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <Card style={{ padding: '24px' }}>
          <h2 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: '0 0 16px', paddingBottom: '12px', borderBottom: `1px solid ${COLORS.borderLight}` }}>Company Details</h2>
          {fields.map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: i < fields.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none' }}>
              <span style={{ fontSize: '12px', color: COLORS.textFaint, fontWeight: '500' }}>{f.label}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.text, textAlign: 'right', maxWidth: '60%' }}>{f.value}</span>
            </div>
          ))}
        </Card>
        <Card style={{ padding: '24px' }}>
          <h2 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: '0 0 16px', paddingBottom: '12px', borderBottom: `1px solid ${COLORS.borderLight}` }}>Users ({users.length})</h2>
          {users.length === 0 ? (
            <p style={{ fontSize: '13px', color: COLORS.textFaint }}>No users found</p>
          ) : (
            users.map((user, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < users.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: '700' }}>
                  {(user.full_name || user.id || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: COLORS.text, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.full_name || 'Unnamed User'}</p>
                  <p style={{ fontSize: '11px', color: COLORS.textFaint, margin: 0 }}>{user.role || 'member'}</p>
                </div>
                <Badge color={user.role === 'admin' ? COLORS.primary : COLORS.textFaint} bg={user.role === 'admin' ? COLORS.primaryLight : COLORS.borderLight} border={user.role === 'admin' ? '#BFDBFE' : COLORS.border}>{user.role || 'member'}</Badge>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  )
}

// ─── NOTIFICATIONS TAB ───────────────────────────────────────────────────────
function NotificationsTab({ notifications, onSave, onDelete, onToggle }) {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ message: '', type: 'info', is_active: true })
  const [saving, setSaving] = useState(false)

  const openNew = () => { setEditing(null); setForm({ message: '', type: 'info', is_active: true, scheduled_at: '', expires_at: '' }); setShowModal(true) }
  const openEdit = (n) => { setEditing(n); setForm({ message: n.message, type: n.type, is_active: n.is_active, scheduled_at: n.scheduled_at ? n.scheduled_at.slice(0, 16) : '', expires_at: n.expires_at ? n.expires_at.slice(0, 16) : '' }); setShowModal(true) }

  const handleSave = async () => {
    if (!form.message.trim()) return
    setSaving(true)
    await onSave(form, editing)
    setShowModal(false)
    setEditing(null)
    setSaving(false)
  }

  return (
    <div>
      <SectionHeader title="Notifications" subtitle="Manage messages shown to all contractors" action={<PrimaryButton onClick={openNew}>+ New Notification</PrimaryButton>} />
      <Card>
        {notifications.length === 0 ? (
          <div style={{ padding: '64px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: COLORS.textFaint, margin: '0 0 16px' }}>No notifications yet</p>
            <PrimaryButton onClick={openNew}>Create your first notification</PrimaryButton>
          </div>
        ) : (
          notifications.map((n, i) => {
            const cfg = notifTypeConfig[n.type] || notifTypeConfig.info
            return (
              <div key={n.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', padding: '16px 24px', alignItems: 'center', gap: '16px', borderBottom: i < notifications.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '500', color: COLORS.text, margin: '0 0 6px' }}>{n.message}</p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Badge color={cfg.color} bg={cfg.bg} border={cfg.border}>{cfg.label}</Badge>
                    <span style={{ fontSize: '11px', color: COLORS.textFaint }}>{new Date(n.created_at).toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
                <Badge color={n.is_active ? COLORS.success : COLORS.textFaint} bg={n.is_active ? COLORS.successLight : COLORS.borderLight} border={n.is_active ? '#BBF7D0' : COLORS.border}>{n.is_active ? 'Active' : 'Inactive'}</Badge>
                <button onClick={() => onToggle(n)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.border}`, background: 'white', color: COLORS.textMid, cursor: 'pointer', whiteSpace: 'nowrap' }}>{n.is_active ? 'Deactivate' : 'Activate'}</button>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => openEdit(n)} style={{ padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.border}`, background: 'white', color: COLORS.primary, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => onDelete(n.id)} style={{ padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.dangerLight}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            )
          })
        )}
      </Card>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '520px', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: COLORS.text, margin: 0 }}>{editing ? 'Edit Notification' : 'New Notification'}</h2>
              <button onClick={() => setShowModal(false)} style={{ width: '32px', height: '32px', borderRadius: '8px', background: COLORS.borderLight, border: 'none', fontSize: '16px', color: COLORS.textMuted, cursor: 'pointer', fontWeight: '600' }}>×</button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '6px' }}>Message *</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="e.g. Scheduled maintenance on 10th June between 2–4am" style={{ width: '100%', height: '100px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'inherit', color: COLORS.text }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '8px' }}>Type</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {Object.entries(notifTypeConfig).map(([val, cfg]) => (
                  <div key={val} onClick={() => setForm({ ...form, type: val })} style={{ flex: 1, padding: '10px', borderRadius: '9px', cursor: 'pointer', textAlign: 'center', background: form.type === val ? cfg.bg : '#F8FAFC', border: `1.5px solid ${form.type === val ? cfg.color : COLORS.border}`, color: form.type === val ? cfg.color : COLORS.textMuted, fontSize: '13px', fontWeight: '600', transition: 'all 0.15s' }}>{cfg.label}</div>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div onClick={() => setForm({ ...form, is_active: !form.is_active })} style={{ width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0, border: `2px solid ${form.is_active ? COLORS.primary : COLORS.border}`, background: form.is_active ? COLORS.primary : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                {form.is_active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg>}
              </div>
              <span style={{ fontSize: '13px', color: COLORS.textMid, fontWeight: '500' }}>Active — visible to contractors immediately</span>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, height: '44px', borderRadius: '10px', background: 'white', border: `1.5px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Cancel</button>
              <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Notification'}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── WEBSITE TAB ─────────────────────────────────────────────────────────────
function WebsiteTab({ siteContent, onSave }) {
  const [content, setContent] = useState({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeSection, setActiveSection] = useState('nav')
  const [pages, setPages] = useState([])
  const [selectedPage, setSelectedPage] = useState(null)
  const [pageForm, setPageForm] = useState({ title: '', slug: '', content: '', is_published: true })
  const [pageMode, setPageMode] = useState(null)
  const [pageSaving, setPageSaving] = useState(false)
  const [pageSaved, setPageSaved] = useState(false)

  useEffect(() => {
    const map = {}
    siteContent.forEach(row => { map[`${row.section}__${row.key}`] = row.value })
    setContent(map)
    fetchPages()
  }, [siteContent])

  const fetchPages = async () => {
    const { data } = await supabase.from('site_pages').select('*').order('created_at', { ascending: true })
    setPages(data || [])
  }

  const get = (section, key) => content[`${section}__${key}`] || ''
  const set = (section, key, value) => setContent(prev => ({ ...prev, [`${section}__${key}`]: value }))

  const handleSave = async () => {
    setSaving(true)
    const updates = Object.entries(content).map(([k, value]) => {
      const [section, key] = k.split('__')
      return { section, key, value }
    })
    for (const u of updates) {
      await supabase.from('site_content').upsert({ section: u.section, key: u.key, value: u.value }, { onConflict: 'section,key' })
    }
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handlePageSave = async () => {
    if (!pageForm.title.trim() || !pageForm.slug.trim()) return
    setPageSaving(true)
    const slug = pageForm.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    const payload = { title: pageForm.title, slug, content: pageForm.content, is_published: pageForm.is_published, updated_at: new Date().toISOString() }
    if (pageMode === 'edit' && selectedPage) {
      await supabase.from('site_pages').update(payload).eq('id', selectedPage.id)
    } else {
      await supabase.from('site_pages').insert([payload])
    }
    await fetchPages()
    setPageMode(null)
    setSelectedPage(null)
    setPageSaving(false)
    setPageSaved(true)
    setTimeout(() => setPageSaved(false), 3000)
  }

  const handlePageDelete = async (page) => {
    if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return
    await supabase.from('site_pages').delete().eq('id', page.id)
    fetchPages()
  }

  const openNewPage = () => { setPageForm({ title: '', slug: '', content: '', is_published: true }); setSelectedPage(null); setPageMode('new') }
  const openEditPage = (page) => { setPageForm({ title: page.title, slug: page.slug, content: page.content || '', is_published: page.is_published }); setSelectedPage(page); setPageMode('edit') }

  const sections = [
    { id: 'nav', label: 'Navigation' },
    { id: 'hero', label: 'Hero' },
    { id: 'features', label: 'Features' },
    { id: 'how', label: 'How it works' },
    { id: 'workorders', label: 'Work Orders' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'cta', label: 'CTA Strip' },
    { id: 'footer', label: 'Footer' },
    { id: 'pages', label: 'Pages' },
  ]

  const fieldStyle = { width: '100%', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '10px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', fontFamily: 'inherit', color: COLORS.text }
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px' }

  const fieldGroup = (label, section, key, multiline = false) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={labelStyle}>{label}</label>
      {multiline
        ? <textarea value={get(section, key)} onChange={e => set(section, key, e.target.value)} style={{ ...fieldStyle, height: '90px', resize: 'vertical' }} />
        : <input type="text" value={get(section, key)} onChange={e => set(section, key, e.target.value)} style={{ ...fieldStyle, height: '42px' }} />
      }
    </div>
  )

  const renderSection = () => {
    switch (activeSection) {
      case 'nav':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Navigation</h3>
            {fieldGroup('Brand Name', 'nav', 'brand_name')}
            {fieldGroup('Menu Link 1', 'nav', 'link_1')}
            {fieldGroup('Menu Link 2', 'nav', 'link_2')}
            {fieldGroup('Menu Link 3', 'nav', 'link_3')}
          </div>
        )
      case 'hero':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Hero Section</h3>
            {fieldGroup('Eyebrow Tag', 'hero', 'eyebrow')}
            {fieldGroup('Headline Line 1', 'hero', 'headline_line1')}
            {fieldGroup('Headline Line 2', 'hero', 'headline_line2')}
            {fieldGroup('Headline Line 3 (orange)', 'hero', 'headline_line3')}
            {fieldGroup('Subheading', 'hero', 'subheading', true)}
            {fieldGroup('Primary Button Text', 'hero', 'cta_primary')}
            {fieldGroup('Secondary Button Text', 'hero', 'cta_secondary')}
            {fieldGroup('Small Note', 'hero', 'note')}
          </div>
        )
      case 'features':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Features Section</h3>
            {fieldGroup('Section Tag', 'features', 'tag')}
            {fieldGroup('Heading', 'features', 'heading')}
            {fieldGroup('Subheading', 'features', 'subheading', true)}
            {[1,2,3,4,5,6].map(n => (
              <div key={n} style={{ background: COLORS.bg, borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', fontWeight: '700', color: COLORS.textFaint, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Feature {n}</p>
                {fieldGroup('Title', 'features', `feat_${n}_title`)}
                {fieldGroup('Description', 'features', `feat_${n}_desc`, true)}
              </div>
            ))}
          </div>
        )
      case 'how':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>How It Works</h3>
            {fieldGroup('Section Tag', 'how', 'tag')}
            {fieldGroup('Heading', 'how', 'heading')}
            {fieldGroup('Subheading', 'how', 'subheading')}
            {[1,2,3,4].map(n => (
              <div key={n} style={{ background: COLORS.bg, borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', fontWeight: '700', color: COLORS.textFaint, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Step {n}</p>
                {fieldGroup('Title', 'how', `step_${n}_title`)}
                {fieldGroup('Description', 'how', `step_${n}_desc`, true)}
              </div>
            ))}
          </div>
        )
      case 'workorders':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Work Orders Section</h3>
            {fieldGroup('Section Tag', 'workorders', 'tag')}
            {fieldGroup('Heading', 'workorders', 'heading')}
            {fieldGroup('Description', 'workorders', 'desc', true)}
            {[1,2,3,4].map(n => fieldGroup(`Bullet Point ${n}`, 'workorders', `bullet_${n}`))}
          </div>
        )
      case 'pricing':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Pricing Section</h3>
            {fieldGroup('Section Tag', 'pricing', 'tag')}
            {fieldGroup('Heading', 'pricing', 'heading')}
            {fieldGroup('Subheading', 'pricing', 'subheading', true)}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Monthly Price (£)</label>
                <input type="number" value={get('pricing', 'monthly_price')} onChange={e => set('pricing', 'monthly_price', e.target.value)} style={{ ...fieldStyle, height: '42px' }} />
              </div>
              <div>
                <label style={labelStyle}>Annual Price (£)</label>
                <input type="number" value={get('pricing', 'annual_price')} onChange={e => set('pricing', 'annual_price', e.target.value)} style={{ ...fieldStyle, height: '42px' }} />
              </div>
              <div>
                <label style={labelStyle}>Trial Length (days)</label>
                <input type="number" value={get('pricing', 'trial_days')} onChange={e => set('pricing', 'trial_days', e.target.value)} style={{ ...fieldStyle, height: '42px' }} />
              </div>
            </div>
            {[1,2,3,4,5,6].map(n => fieldGroup(`Feature ${n}`, 'pricing', `feat_${n}`))}
          </div>
        )
      case 'cta':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>CTA Strip</h3>
            {fieldGroup('Heading Line 1', 'cta', 'heading_line1')}
            {fieldGroup('Heading Line 2 (orange)', 'cta', 'heading_line2')}
            {fieldGroup('Button Text', 'cta', 'button')}
            {fieldGroup('Small Note', 'cta', 'note')}
          </div>
        )
      case 'footer':
        return (
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: '0 0 20px' }}>Footer</h3>
            {fieldGroup('Brand Description', 'footer', 'description', true)}
            {fieldGroup('Product Link 1', 'footer', 'link_1_label')}
            {fieldGroup('Product Link 2', 'footer', 'link_2_label')}
            {fieldGroup('Product Link 3', 'footer', 'link_3_label')}
            {fieldGroup('Product Link 4', 'footer', 'link_4_label')}
            {fieldGroup('Legal Link 1', 'footer', 'legal_1')}
            {fieldGroup('Legal Link 2', 'footer', 'legal_2')}
            {fieldGroup('Legal Link 3', 'footer', 'legal_3')}
            {fieldGroup('Copyright Text', 'footer', 'copyright')}
          </div>
        )
      case 'pages':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Pages</h3>
              {!pageMode && <button onClick={openNewPage} style={{ height: '36px', padding: '0 16px', borderRadius: '8px', background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, color: 'white', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer' }}>+ New Page</button>}
            </div>

            {!pageMode && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pages.length === 0 ? (
                  <p style={{ fontSize: '13px', color: COLORS.textFaint, textAlign: 'center', padding: '32px 0' }}>No pages yet</p>
                ) : (
                  pages.map(page => (
                    <div key={page.id} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: COLORS.text, margin: '0 0 3px' }}>{page.title}</p>
                        <p style={{ fontSize: '12px', color: COLORS.textFaint, margin: 0 }}>/{page.slug}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', background: page.is_published ? COLORS.successLight : COLORS.borderLight, color: page.is_published ? COLORS.success : COLORS.textFaint, border: `1px solid ${page.is_published ? '#BBF7D0' : COLORS.border}` }}>{page.is_published ? 'Published' : 'Draft'}</span>
                        <button onClick={() => openEditPage(page)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.border}`, background: 'white', color: COLORS.primary, cursor: 'pointer' }}>Edit</button>
                        <button onClick={() => handlePageDelete(page)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.dangerLight}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: 'pointer' }}>Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {pageMode && (
              <div>
                <button onClick={() => { setPageMode(null); setSelectedPage(null) }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: COLORS.textMuted, fontWeight: '600', padding: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}>← Back to Pages</button>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Page Title *</label>
                  <input type="text" value={pageForm.title} onChange={e => setPageForm({ ...pageForm, title: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })} placeholder="e.g. Privacy Policy" style={{ ...fieldStyle, height: '42px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>URL Slug *</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.textFaint, whiteSpace: 'nowrap' }}>quotingpro.co.uk/</span>
                    <input type="text" value={pageForm.slug} onChange={e => setPageForm({ ...pageForm, slug: e.target.value })} placeholder="privacy-policy" style={{ ...fieldStyle, height: '42px' }} />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Content</label>
                  <textarea value={pageForm.content} onChange={e => setPageForm({ ...pageForm, content: e.target.value })} placeholder="Enter page content here. You can use basic HTML if needed." style={{ ...fieldStyle, height: '320px', resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                  <div onClick={() => setPageForm({ ...pageForm, is_published: !pageForm.is_published })} style={{ width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0, border: `2px solid ${pageForm.is_published ? COLORS.primary : COLORS.border}`, background: pageForm.is_published ? COLORS.primary : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    {pageForm.is_published && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg>}
                  </div>
                  <span style={{ fontSize: '13px', color: COLORS.textMid, fontWeight: '500' }}>Published — visible on the website</span>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setPageMode(null); setSelectedPage(null) }} style={{ flex: 1, height: '44px', borderRadius: '10px', background: 'white', border: `1.5px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handlePageSave} disabled={pageSaving || !pageForm.title.trim() || !pageForm.slug.trim()} style={{ flex: 1, height: '44px', borderRadius: '10px', background: pageSaved ? '#15803D' : pageSaving || !pageForm.title.trim() || !pageForm.slug.trim() ? '#CBD5E1' : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, color: 'white', fontSize: '14px', fontWeight: '700', border: 'none', cursor: pageSaving ? 'not-allowed' : 'pointer' }}>
                    {pageSaving ? 'Saving...' : pageSaved ? '✓ Saved' : pageMode === 'edit' ? 'Save Changes' : 'Create Page'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div>
      <SectionHeader
        title="Website"
        subtitle="Edit all marketing site content — changes save to database and appear on the live site"
        action={
          activeSection !== 'pages' ? (
            <button onClick={handleSave} disabled={saving} style={{ height: '40px', padding: '0 24px', borderRadius: '9px', background: saved ? '#15803D' : saving ? '#CBD5E1' : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, color: 'white', fontSize: '13px', fontWeight: '700', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
              {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
            </button>
          ) : null
        }
      />
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '24px', alignItems: 'start' }}>
        <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
          {sections.map((s, i) => (
            <div key={s.id} onClick={() => { setActiveSection(s.id); setPageMode(null); setSelectedPage(null) }} style={{ padding: '11px 16px', fontSize: '13px', fontWeight: activeSection === s.id ? '600' : '500', color: activeSection === s.id ? COLORS.primary : COLORS.textMid, background: activeSection === s.id ? COLORS.primaryLight : 'transparent', cursor: 'pointer', transition: 'all 0.15s', borderBottom: i < sections.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={e => { if (activeSection !== s.id) e.currentTarget.style.background = COLORS.bgHover }}
              onMouseLeave={e => { if (activeSection !== s.id) e.currentTarget.style.background = 'transparent' }}
            >
              {s.label}
              {s.id === 'pages' && pages.length > 0 && <span style={{ fontSize: '11px', color: COLORS.textFaint }}>({pages.length})</span>}
            </div>
          ))}
        </div>
        <Card style={{ padding: '28px' }}>
          {renderSection()}
        </Card>
      </div>
    </div>
  )
}

// ─── APP SETTINGS TAB ────────────────────────────────────────────────────────
function AppSettingsTab() {
  const sections = [
    { title: 'Quote Statuses', description: 'Manage the statuses available on quotes', status: 'Live', statusColor: COLORS.success, statusBg: COLORS.successLight, content: (<div>{['Draft', 'Sent', 'Accepted', 'Rejected'].map((s, i) => (<div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 3 ? `1px solid ${COLORS.borderLight}` : 'none' }}><span style={{ fontSize: '13px', fontWeight: '500', color: COLORS.text }}>{s}</span><span style={{ fontSize: '12px', color: COLORS.textFaint }}>Default</span></div>))}</div>) },
    { title: 'Stripe & Billing Plans', description: 'Configure subscription plans and payment settings', status: 'Coming Soon', statusColor: COLORS.warning, statusBg: COLORS.warningLight, content: <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0 }}>Manage Free, Pro, and Enterprise plans. Configure Stripe keys and pricing.</p> },
    { title: 'Email Templates', description: 'Edit transactional emails sent to contractors', status: 'Coming Soon', statusColor: COLORS.warning, statusBg: COLORS.warningLight, content: <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0 }}>Customise welcome emails, quote notifications, and billing emails.</p> },
  ]

  return (
    <div>
      <SectionHeader title="App Settings" subtitle="Global settings that affect all contractors" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '680px' }}>
        {sections.map((s, i) => (
          <Card key={i}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: '0 0 3px' }}>{s.title}</h3>
                <p style={{ fontSize: '12px', color: COLORS.textFaint, margin: 0 }}>{s.description}</p>
              </div>
              <Badge color={s.statusColor} bg={s.statusBg} border="transparent">{s.status}</Badge>
            </div>
            <div style={{ padding: '16px 24px' }}>{s.content}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}

// ─── PAYMENTS TAB ────────────────────────────────────────────────────────────
function PaymentsTab({ companies, onExtendTrial }) {
  const [extendModal, setExtendModal] = useState(null)
  const [extendDays, setExtendDays] = useState(7)
  const [extending, setExtending] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [search, setSearch] = useState('')
  const now = new Date()

  const getStatus = (company) => {
    const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null
    const paymentFailed = company.payment_failed_at ? new Date(company.payment_failed_at) : null
    const sixMonthsAfterFail = paymentFailed ? new Date(paymentFailed.getTime() + 180 * 24 * 60 * 60 * 1000) : null
    if (company.plan_status === 'active') return 'active'
    if (company.plan_status === 'cancelled') return 'cancelled'
    if (paymentFailed) { if (sixMonthsAfterFail < now) return 'pending_deletion'; return 'locked' }
    if (trialEnd && trialEnd > now) return 'trial'
    if (trialEnd && trialEnd <= now) return 'trial_expired'
    return 'trial'
  }

  const getDaysRemaining = (company) => {
    const status = getStatus(company)
    const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null
    const paymentFailed = company.payment_failed_at ? new Date(company.payment_failed_at) : null
    const deletionDate = paymentFailed ? new Date(paymentFailed.getTime() + 180 * 24 * 60 * 60 * 1000) : null
    if (status === 'trial' && trialEnd) { const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)); return { label: `${days}d left`, color: days <= 3 ? COLORS.danger : COLORS.warning } }
    if (status === 'trial_expired') return { label: 'Expired', color: COLORS.danger }
    if (status === 'locked' && deletionDate) { const days = Math.ceil((deletionDate - now) / (1000 * 60 * 60 * 24)); return { label: `Deletes in ${days}d`, color: COLORS.danger } }
    if (status === 'pending_deletion') return { label: 'Due for deletion', color: COLORS.danger }
    if (status === 'active') return { label: 'Paid', color: COLORS.success }
    if (status === 'cancelled') return { label: 'Cancelled', color: COLORS.textFaint }
    return { label: '—', color: COLORS.textFaint }
  }

  const statusConfig = {
    trial:            { label: 'Trial',            color: COLORS.primary,   bg: COLORS.primaryLight, border: '#BFDBFE' },
    trial_expired:    { label: 'Trial Expired',    color: COLORS.danger,    bg: COLORS.dangerLight,  border: '#FECDD3' },
    active:           { label: 'Active',           color: COLORS.success,   bg: COLORS.successLight, border: '#BBF7D0' },
    locked:           { label: 'Locked',           color: COLORS.warning,   bg: COLORS.warningLight, border: '#FDE68A' },
    pending_deletion: { label: 'Pending Deletion', color: COLORS.danger,    bg: COLORS.dangerLight,  border: '#FECDD3' },
    cancelled:        { label: 'Cancelled',        color: COLORS.textFaint, bg: COLORS.borderLight,  border: COLORS.border },
  }

  const statusCounts = companies.reduce((acc, c) => { const s = getStatus(c); acc[s] = (acc[s] || 0) + 1; return acc }, {})
  const filtered = companies.filter(c => { const s = getStatus(c); return (filterStatus === 'all' || s === filterStatus) && c.company_name?.toLowerCase().includes(search.toLowerCase()) }).sort((a, b) => { const order = { pending_deletion: 0, locked: 1, trial_expired: 2, trial: 3, active: 4, cancelled: 5 }; return (order[getStatus(a)] ?? 9) - (order[getStatus(b)] ?? 9) })

  const handleExtend = async () => {
    if (!extendModal) return
    setExtending(true)
    await onExtendTrial(extendModal, extendDays)
    setExtendModal(null); setExtendDays(7); setExtending(false)
  }

  return (
    <div>
      <SectionHeader title="Payments" subtitle="Manage contractor subscriptions and trials" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '28px' }}>
        {[
          { label: 'Active Subscribers', value: statusCounts.active || 0, color: COLORS.success },
          { label: 'On Trial', value: statusCounts.trial || 0, color: COLORS.primary },
          { label: 'Trial Expired', value: (statusCounts.trial_expired || 0) + (statusCounts.locked || 0), color: COLORS.warning },
          { label: 'Pending Deletion', value: statusCounts.pending_deletion || 0, color: COLORS.danger },
        ].map((s, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 8px' }}>{s.label}</p>
            <p style={{ fontSize: '32px', fontWeight: '800', color: COLORS.text, margin: 0, letterSpacing: '-1px' }}>{s.value}</p>
            <div style={{ height: '3px', borderRadius: '999px', background: COLORS.borderLight, marginTop: '10px' }}>
              <div style={{ height: '100%', borderRadius: '999px', background: s.color, width: companies.length > 0 ? `${(s.value / companies.length) * 100}%` : '0%' }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: '13px', fontWeight: '700', color: COLORS.primary, margin: '0 0 2px' }}>Stripe not connected</p>
          <p style={{ fontSize: '12px', color: '#3B82F6', margin: 0 }}>Connect Stripe to start collecting payments automatically.</p>
        </div>
        <button style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', background: COLORS.primary, color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', opacity: 0.6 }}>Connect Stripe — coming soon</button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company..." style={{ flex: 1, height: '38px' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ height: '38px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '0 12px', fontSize: '13px', color: COLORS.textMid, background: 'white', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}>
          <option value="all">All statuses</option>
          <option value="trial">Trial</option>
          <option value="trial_expired">Trial Expired</option>
          <option value="active">Active</option>
          <option value="locked">Locked</option>
          <option value="pending_deletion">Pending Deletion</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: 'white', borderRadius: '14px', border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'visible' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 130px 150px', gap: '16px', padding: '10px 24px', background: '#F8FAFC', borderBottom: `1px solid ${COLORS.border}`, borderRadius: '14px 14px 0 0' }}>
          {['Company', 'Plan Status', 'Timeline', 'Extended By', 'Actions'].map((h, i) => (
            <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: COLORS.textFaint }}>No companies found</div>
        ) : (
          filtered.map((company, i) => {
            const status = getStatus(company)
            const cfg = statusConfig[status] || statusConfig.trial
            const timeline = getDaysRemaining(company)
            const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null
            return (
              <div key={company.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 130px 150px', gap: '16px', padding: '14px 24px', alignItems: 'center', borderBottom: i < filtered.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = COLORS.bgHover}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: COLORS.text, margin: '0 0 2px' }}>{company.company_name}</p>
                  <p style={{ fontSize: '11px', color: COLORS.textFaint, margin: 0 }}>Joined {new Date(company.created_at).toLocaleDateString('en-GB')}</p>
                </div>
                <Badge color={cfg.color} bg={cfg.bg} border={cfg.border}>{cfg.label}</Badge>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '600', color: timeline.color, margin: '0 0 2px' }}>{timeline.label}</p>
                  {trialEnd && <p style={{ fontSize: '11px', color: COLORS.textFaint, margin: 0 }}>Trial ends {trialEnd.toLocaleDateString('en-GB')}</p>}
                </div>
                <span style={{ fontSize: '12px', color: company.trial_extended_days > 0 ? COLORS.primary : COLORS.textFaint, fontWeight: company.trial_extended_days > 0 ? '600' : '400' }}>{company.trial_extended_days > 0 ? `+${company.trial_extended_days} days` : '—'}</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {(status === 'trial' || status === 'trial_expired' || status === 'locked') && <button onClick={() => { setExtendModal(company); setExtendDays(7) }} style={{ padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.border}`, background: 'white', color: COLORS.primary, cursor: 'pointer', whiteSpace: 'nowrap' }}>Extend Trial</button>}
                  {status === 'active' && <span style={{ fontSize: '12px', color: COLORS.textFaint }}>Stripe managed</span>}
                  {status === 'pending_deletion' && <button onClick={() => { setExtendModal(company); setExtendDays(30) }} style={{ padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: `1.5px solid ${COLORS.dangerLight}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: 'pointer', whiteSpace: 'nowrap' }}>Rescue Account</button>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {extendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '440px', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '800', color: COLORS.text, margin: '0 0 6px' }}>Extend Trial</h2>
            <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: '0 0 24px' }}>Extending trial for <strong>{extendModal.company_name}</strong></p>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '10px' }}>How many days to add?</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                {[7, 14, 30].map(d => <button key={d} onClick={() => setExtendDays(d)} style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: extendDays === d ? COLORS.primaryLight : 'white', border: `1.5px solid ${extendDays === d ? COLORS.primary : COLORS.border}`, color: extendDays === d ? COLORS.primary : COLORS.textMid }}>{d} days</button>)}
              </div>
              <input type="number" min="1" max="365" value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value) || 1)} style={{ width: '90px', height: '38px', borderRadius: '8px', border: `1.5px solid ${COLORS.border}`, padding: '0 12px', fontSize: '14px', fontWeight: '600', outline: 'none', textAlign: 'center', fontFamily: 'inherit' }} />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setExtendModal(null)} style={{ flex: 1, height: '44px', borderRadius: '10px', background: 'white', border: `1.5px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Cancel</button>
              <PrimaryButton onClick={handleExtend} disabled={extending}>{extending ? 'Saving...' : `Add ${extendDays} Days`}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function AdminDashboard({ session }) {
  const navigate = useNavigate()
  const [stats, setStats] = useState({ companies: 0, users: 0, quotes: 0 })
  const [companies, setCompanies] = useState([])
  const [notifications, setNotifications] = useState([])
  const [siteContent, setSiteContent] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [companyUsers, setCompanyUsers] = useState([])
  const [companyNotifTarget, setCompanyNotifTarget] = useState(null) // eslint-disable-line no-unused-vars
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(null)
  const [suspendModal, setSuspendModal] = useState(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [suspending, setSuspending] = useState(false)
  const [emailModal, setEmailModal] = useState(null)
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' })

  useEffect(() => {
    fetchData()
    ;['png', 'jpg', 'jpeg', 'svg', 'webp'].forEach(ext => {
      const { data } = supabase.storage.from('logos').getPublicUrl(`admin-branding.${ext}`)
      if (data?.publicUrl) {
        const img = new Image()
        img.onload = () => setBrandingLogoUrl(data.publicUrl + '?t=' + Date.now())
        img.src = data.publicUrl
      }
    })
  }, [])

  const fetchData = async () => {
    const [{ data: co }, { data: pr }, { data: qu }, { data: no }, { data: sc }] = await Promise.all([
      supabase.from('companies').select('*').order('created_at', { ascending: false }),
      supabase.from('profiles').select('*'),
      supabase.from('quotes').select('id'),
      supabase.from('notifications').select('*').order('created_at', { ascending: false }),
      supabase.from('site_content').select('*'),
    ])
    setCompanies(co || [])
    setStats({ companies: co?.length || 0, users: pr?.length || 0, quotes: qu?.length || 0 })
    setNotifications(no || [])
    setSiteContent(sc || [])
    setLoading(false)
  }

  const fetchCompanyUsers = async (companyId) => {
    const { data } = await supabase.from('profiles').select('*').eq('company_id', companyId)
    setCompanyUsers(data || [])
  }

  const handleSelectCompany = async (company) => { setSelectedCompany(company); await fetchCompanyUsers(company.id) }

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Delete "${company.company_name}"? This cannot be undone.`)) return
    await supabase.from('profiles').delete().eq('company_id', company.id)
    const { error } = await supabase.from('companies').delete().eq('id', company.id)
    if (error) { alert(`Could not delete company: ${error.message}`); return }
    setSelectedCompany(null); fetchData()
  }

  const handleSuspendCompany = async () => {
    if (!suspendModal) return
    setSuspending(true)
    await supabase.from('companies').update({ status: 'suspended', suspend_reason: suspendReason || null }).eq('id', suspendModal.id)
    setSuspendModal(null); setSuspendReason(''); setSuspending(false); fetchData()
  }

  const handleReactivateCompany = async (company) => {
    if (!window.confirm(`Reactivate "${company.company_name}"?`)) return
    await supabase.from('companies').update({ status: 'active', suspend_reason: null }).eq('id', company.id)
    fetchData()
  }

  const handleSaveNotification = async (form, editing) => {
    const payload = { message: form.message, type: form.type, is_active: form.scheduled_at ? false : form.is_active, scheduled_at: form.scheduled_at || null, expires_at: form.expires_at || null }
    if (editing) { await supabase.from('notifications').update(payload).eq('id', editing.id) }
    else { await supabase.from('notifications').insert([payload]) }
    fetchData()
  }

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Delete this notification?')) return
    await supabase.from('notifications').delete().eq('id', id); fetchData()
  }

  const handleToggleNotification = async (notif) => {
    await supabase.from('notifications').update({ is_active: !notif.is_active }).eq('id', notif.id); fetchData()
  }

  const handleExtendTrial = async (company, days) => {
    const currentEnd = new Date(company.trial_ends_at || company.created_at)
    const newEnd = new Date(currentEnd.getTime() + days * 24 * 60 * 60 * 1000)
    await supabase.from('companies').update({ trial_ends_at: newEnd.toISOString(), trial_extended_days: (company.trial_extended_days || 0) + days, plan_status: 'trial' }).eq('id', company.id)
    fetchData()
  }

  const handleSignOut = async () => { await supabase.auth.signOut(); navigate('/admin/login') }

  const handleBrandingUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const ext = file.name.split('.').pop()
    const path = `admin-branding.${ext}`
    const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true })
    if (error) { alert('Upload failed: ' + error.message); return }
    const { data } = supabase.storage.from('logos').getPublicUrl(path)
    setBrandingLogoUrl(data.publicUrl + '?t=' + Date.now())
  }

  const sidebarItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'companies', label: 'Companies' },
    { id: 'payments', label: 'Payments' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'website', label: 'Website' },
    { id: 'settings', label: 'App Settings' },
  ]

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'DM Sans', Inter, -apple-system, sans-serif", background: COLORS.bg }}>

      {/* ── SIDEBAR ── */}
      <div style={{ width: '240px', minHeight: '100vh', background: 'white', borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, bottom: 0 }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #F1F5F9' }}>
          <input type="file" accept="image/*" id="branding-logo-upload" style={{ display: 'none' }} onChange={handleBrandingUpload} />
          <div onClick={() => document.getElementById('branding-logo-upload').click()} title="Click to upload or change logo" style={{ width: '100%', height: '56px', borderRadius: '10px', border: `1.5px dashed ${COLORS.border}`, background: '#FAFBFC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '12px', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.primary}
            onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
          >
            {brandingLogoUrl
              ? <img src={brandingLogoUrl} alt="Admin logo" style={{ maxHeight: '48px', maxWidth: '196px', objectFit: 'contain', padding: '4px' }} />
              : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '800', fontSize: '14px', flexShrink: 0 }}>Q</div>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: COLORS.text, margin: 0 }}>QuotingPro</p>
                    <p style={{ fontSize: '10px', color: COLORS.textFaint, margin: 0 }}>Click to upload logo</p>
                  </div>
                </div>
              )
            }
          </div>
          <p style={{ fontSize: '11px', color: COLORS.textFaint, margin: 0, textAlign: 'center' }}>Admin Panel</p>
        </div>

        <div style={{ padding: '16px 0', flex: 1 }}>
          <p style={{ fontSize: '10px', fontWeight: '700', color: COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '1px', padding: '0 20px', margin: '0 0 6px' }}>Menu</p>
          {sidebarItems.map((item) => (
            <div key={item.id} onClick={() => { setActiveTab(item.id); setSelectedCompany(null) }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px', margin: '1px 10px', borderRadius: '8px', background: activeTab === item.id ? COLORS.primaryLight : 'transparent', color: activeTab === item.id ? COLORS.primary : COLORS.textMuted, fontWeight: activeTab === item.id ? '600' : '500', fontSize: '13.5px', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { if (activeTab !== item.id) e.currentTarget.style.background = COLORS.borderLight }}
              onMouseLeave={e => { if (activeTab !== item.id) e.currentTarget.style.background = 'transparent' }}
            >
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: activeTab === item.id ? COLORS.primary : COLORS.border, flexShrink: 0 }} />
              {item.label}
              {item.id === 'notifications' && notifications.filter(n => n.is_active).length > 0 && (
                <span style={{ marginLeft: 'auto', background: COLORS.primary, color: 'white', borderRadius: '999px', fontSize: '10px', fontWeight: '700', padding: '1px 7px' }}>{notifications.filter(n => n.is_active).length}</span>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding: '12px 16px 24px', borderTop: `1px solid ${COLORS.borderLight}` }}>
          <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#F8FAFC', border: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{session?.user?.email?.[0]?.toUpperCase()}</div>
              <p style={{ fontSize: '12px', fontWeight: '600', color: COLORS.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session?.user?.email}</p>
            </div>
            <button onClick={handleSignOut} style={{ width: '100%', padding: '6px', borderRadius: '6px', background: 'white', border: `1px solid ${COLORS.border}`, fontSize: '12px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Sign out</button>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px', maxWidth: 'calc(100vw - 240px)' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.textFaint, fontSize: '14px' }}>Loading...</div>
        ) : (
          <>
            {activeTab === 'overview' && <OverviewTab stats={stats} notifications={notifications} onTabChange={setActiveTab} />}
            {activeTab === 'companies' && !selectedCompany && <CompaniesTab companies={companies} loading={loading} onSelectCompany={handleSelectCompany} onDeleteCompany={handleDeleteCompany} onNotifyCompany={(c) => { setCompanyNotifTarget(c); setActiveTab('notifications') }} onSuspendCompany={(c) => { setSuspendModal(c); setSuspendReason('') }} onReactivateCompany={handleReactivateCompany} onEmailCompany={(c) => { setEmailModal(c); setEmailForm({ subject: '', body: '' }) }} />}
            {activeTab === 'companies' && selectedCompany && <CompanyDetailTab company={selectedCompany} users={companyUsers} onBack={() => setSelectedCompany(null)} onNotify={() => { setCompanyNotifTarget(selectedCompany); setActiveTab('notifications') }} onDelete={() => handleDeleteCompany(selectedCompany)} onSuspend={() => { setSuspendModal(selectedCompany); setSuspendReason('') }} onReactivate={() => handleReactivateCompany(selectedCompany)} />}
            {activeTab === 'notifications' && <NotificationsTab notifications={notifications} onSave={handleSaveNotification} onDelete={handleDeleteNotification} onToggle={handleToggleNotification} />}
            {activeTab === 'website' && <WebsiteTab siteContent={siteContent} onSave={fetchData} />}
            {activeTab === 'settings' && <AppSettingsTab />}
            {activeTab === 'payments' && <PaymentsTab companies={companies} onExtendTrial={handleExtendTrial} />}
          </>
        )}
      </div>

      {/* ── SUSPEND MODAL ── */}
      {suspendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '800', color: COLORS.text, margin: '0 0 6px' }}>Suspend Account</h2>
            <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: '0 0 20px' }}>Suspending <strong>{suspendModal.company_name}</strong> will block all their users from accessing the app.</p>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '6px' }}>Reason (optional)</label>
              <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="e.g. Non-payment, terms violation..." style={{ width: '100%', height: '90px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '10px 14px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'none', fontFamily: 'inherit', color: COLORS.text }} />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setSuspendModal(null); setSuspendReason('') }} style={{ flex: 1, height: '44px', borderRadius: '10px', background: 'white', border: `1.5px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSuspendCompany} disabled={suspending} style={{ flex: 1, height: '44px', borderRadius: '10px', background: suspending ? '#CBD5E1' : COLORS.danger, color: 'white', fontSize: '14px', fontWeight: '700', border: 'none', cursor: suspending ? 'not-allowed' : 'pointer' }}>{suspending ? 'Suspending...' : 'Suspend Account'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EMAIL MODAL ── */}
      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '520px', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '800', color: COLORS.text, margin: '0 0 6px' }}>Send Email</h2>
            <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: '0 0 20px' }}>To: <strong>{emailModal.company_name}</strong></p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '6px' }}>Subject *</label>
              <input type="text" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="e.g. Important update about your QuotingPro account" style={{ width: '100%', height: '42px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '0 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', fontFamily: 'inherit', color: COLORS.text }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: COLORS.textMid, marginBottom: '6px' }}>Message *</label>
              <textarea value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} placeholder="Write your message here..." style={{ width: '100%', height: '160px', borderRadius: '9px', border: `1.5px solid ${COLORS.border}`, padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'inherit', color: COLORS.text }} />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setEmailModal(null)} style={{ flex: 1, height: '44px', borderRadius: '10px', background: 'white', border: `1.5px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '600', color: COLORS.textMuted, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { window.location.href = `mailto:${emailModal.email || ''}?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(emailForm.body)}`; setEmailModal(null) }} disabled={!emailForm.subject.trim() || !emailForm.body.trim()} style={{ flex: 1, height: '44px', borderRadius: '10px', background: !emailForm.subject.trim() || !emailForm.body.trim() ? '#CBD5E1' : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, color: 'white', fontSize: '14px', fontWeight: '700', border: 'none', cursor: !emailForm.subject.trim() || !emailForm.body.trim() ? 'not-allowed' : 'pointer' }}>Open in Email Client</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}